import { useCallback, useEffect, useMemo, useState } from "react";

import { fetchMyTeam } from "../services/myTeamApi";
import { readApiErrorMessage } from "../services/apiClient";
import { PLATFORM_NAME } from "../brand";
import "./MyTeam.css";

/**
 * My Team — the subscribers this marketing user referred.
 *
 * There is no referrer selector on this page, and there is nothing to put in
 * one. The backend derives whose team this is from the authenticated session
 * and refuses to read a referrer from the request at all, so the page has no
 * identity to hold and no identity to send.
 *
 * `status` and `search` are sent to the server rather than applied here: the
 * totals have to describe the whole team while the table shows the filtered
 * view, and only the server can do both without the browser holding rows it
 * is not showing.
 */

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "ACTIVE", label: "Active" },
  { value: "EXPIRED", label: "Expired" },
];

/* Why a team can be empty, said plainly. An empty table with no explanation
   reads as "you have no referrals", which is wrong and unactionable when the
   real answer is that a record has not been set up yet. */
const LINK_EXPLANATIONS = {
  not_found: {
    title: "No referral record yet",
    body: `No referrer in ${PLATFORM_NAME} carries your email address. An administrator can add one, and your team will appear here the next time you open this page.`,
  },
  no_email: {
    title: "No email on your account",
    body: "Your account has no email address to match a referrer record against. Ask an administrator to check it.",
  },
  claimed_by_another_account: {
    title: "Already linked to another account",
    body: "A referrer record with your email address is already linked to a different sign-in. An administrator needs to resolve that before this page can show a team.",
  },
};

function currency(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return `$${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Whole days from today until `date`, or null once it has passed. Used only
 * to warn about a renewal that is close, never to decide status — the server
 * already decided that.
 */
function daysUntil(value) {
  if (!value) return null;
  const end = new Date(`${value}T00:00:00Z`).getTime();
  if (Number.isNaN(end)) return null;
  const now = Date.now();
  const days = Math.ceil((end - now) / 86400000);
  return days >= 0 ? days : null;
}

function MyTeam() {
  const [team, setTeam] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const load = useCallback(
    async (signal) => {
      setIsLoading(true);
      try {
        const data = await fetchMyTeam({ status, search: appliedSearch }, { signal });
        setTeam(data);
        setError(null);
      } catch (loadError) {
        if (loadError.name === "CanceledError" || loadError.name === "AbortError") return;
        setError(readApiErrorMessage(loadError, "Could not load your team."));
      } finally {
        setIsLoading(false);
      }
    },
    [status, appliedSearch]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const summary = team?.summary;
  const members = useMemo(() => team?.members || [], [team]);

  function onSearchSubmit(event) {
    event.preventDefault();
    setAppliedSearch(search.trim());
  }

  const unlinked = team && !team.linked;
  const explanation = unlinked
    ? LINK_EXPLANATIONS[team.link_status] || LINK_EXPLANATIONS.not_found
    : null;

  return (
    <main className="team-page">
      <header className="team-intro">
        <span className="team-intro__eyebrow">Marketing</span>
        <h1>My Team</h1>
        <p>
          {team?.referrer ? (
            <>
              The subscribers you referred, recorded under{" "}
              <strong>{team.referrer.name}</strong>.
            </>
          ) : (
            "The subscribers you referred."
          )}
        </p>
      </header>

      {error ? (
        <div className="team-notice team-notice--error" role="alert">
          {error}
        </div>
      ) : null}

      {explanation ? (
        <div className="team-notice" role="status">
          <strong>{explanation.title}</strong>
          <span>{explanation.body}</span>
        </div>
      ) : null}

      {summary ? (
        <section className="team-stats" aria-label="Team summary">
          <Stat label="Referrals" value={String(summary.total)} />
          <Stat label="Active" value={String(summary.active)} tone="positive" />
          <Stat label="Expired" value={String(summary.expired)} />
          <Stat
            label="Your commission"
            value={currency(summary.total_commission)}
            tone={summary.total_commission > 0 ? "positive" : undefined}
            detail="Total agreed across every referral"
          />
        </section>
      ) : null}

      {team?.linked ? (
        <section className="team-card">
          <div className="team-card__head">
            <h2>Your referrals</h2>
            <div className="team-controls">
              <form className="team-search" onSubmit={onSearchSubmit} role="search">
                <label className="team-visually-hidden" htmlFor="team-search-input">
                  Search your referrals
                </label>
                <input
                  id="team-search-input"
                  type="search"
                  placeholder="Search by name or phone"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                <button type="submit" className="team-btn">
                  Search
                </button>
              </form>

              <div
                className="team-filter"
                role="group"
                aria-label="Filter by subscription status"
              >
                {STATUS_FILTERS.map((option) => (
                  <button
                    key={option.value || "all"}
                    type="button"
                    className={`team-filter__option${
                      status === option.value ? " is-active" : ""
                    }`}
                    aria-pressed={status === option.value}
                    onClick={() => setStatus(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="team-table-wrap">
            <table className="team-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Contact</th>
                  <th scope="col">Status</th>
                  <th scope="col">Started</th>
                  <th scope="col">Expires</th>
                  <th scope="col">Subscription</th>
                  <th scope="col">Your commission</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && members.length === 0 ? (
                  <tr>
                    <td colSpan={7}>Loading your team…</td>
                  </tr>
                ) : members.length === 0 ? (
                  <tr>
                    <td colSpan={7}>
                      {appliedSearch || status
                        ? "No referrals match this filter."
                        : "No referrals recorded yet."}
                    </td>
                  </tr>
                ) : (
                  members.map((member) => {
                    const remaining = daysUntil(member.subscription_expiry_date);
                    const isActive = member.subscription_status === "ACTIVE";
                    return (
                      <tr key={member.id}>
                        <td data-label="Name" className="team-table__name">
                          {member.full_name}
                        </td>
                        <td data-label="Contact">{member.phone_number}</td>
                        <td data-label="Status">
                          <span
                            className={`team-status team-status--${
                              isActive ? "active" : "expired"
                            }`}
                          >
                            {isActive ? "Active" : "Expired"}
                          </span>
                        </td>
                        <td data-label="Started">
                          {formatDate(member.subscription_start_date)}
                        </td>
                        <td data-label="Expires">
                          {formatDate(member.subscription_expiry_date)}
                          {isActive && remaining !== null && remaining <= 30 ? (
                            <span className="team-table__soon">
                              {remaining === 0 ? "today" : `in ${remaining}d`}
                            </span>
                          ) : null}
                        </td>
                        <td data-label="Subscription">
                          {currency(member.subscription_amount)}
                          <span className="team-table__months">
                            {member.subscription_duration_months} mo
                          </span>
                        </td>
                        <td data-label="Your commission" className="team-table__commission">
                          {currency(member.referrer_commission)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </main>
  );
}

function Stat({ label, value, detail, tone }) {
  return (
    <div className="team-stat">
      <span className="team-stat__label">{label}</span>
      <strong className={tone ? `is-${tone}` : undefined}>{value}</strong>
      {detail ? <span className="team-stat__detail">{detail}</span> : null}
    </div>
  );
}

export default MyTeam;
